import axios, {
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios';
import { decryptPayload, encryptPayload } from '../interceptor/crypto';

export interface SecureAxiosRequestConfig extends AxiosRequestConfig {
    skipEncryption?: boolean;
    skipDecryption?: boolean;
}

type SecureInternalAxiosRequestConfig = InternalAxiosRequestConfig & {
    skipEncryption?: boolean;
    skipDecryption?: boolean;
};

class Request {
    private axiosInstance: AxiosInstance;
    private baseURL: string;
    private basicAuthToken: string;

    constructor(baseURL: string = process.env.REACT_APP_API_URL || 'https://ciclii-api.onrender.com') {
        this.baseURL = baseURL;

        // Generar token de autenticación básica
        const username = process.env.REACT_APP_BASIC_AUTH_USER || '';
        const password = process.env.REACT_APP_BASIC_AUTH_PASS || '';
        this.basicAuthToken = btoa(`${username}:${password}`);

        this.axiosInstance = axios.create({
            baseURL: this.baseURL,
            timeout: 30000,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Basic ${this.basicAuthToken}`,
            },
        });

        this.setupInterceptors();
    }

    private setupInterceptors(): void {
        this.axiosInstance.interceptors.request.use((config: SecureInternalAxiosRequestConfig) => {
            if (config.skipEncryption || config.data === undefined) {
                return config;
            }

            const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
            if (isFormData) {
                return config;
            }

            try {
                const encrypted = encryptPayload(config.data);
                config.data = { data: encrypted };
            } catch (error) {
                console.error('No fue posible encriptar el payload de la solicitud.', error);
                throw error;
            }

            return config;
        });

        // Interceptor de response - manejar errores globalmente y desencriptar
        this.axiosInstance.interceptors.response.use(
            (response) => {
                const secureConfig = response.config as SecureInternalAxiosRequestConfig;

                if (!secureConfig?.skipDecryption && response?.data && typeof response.data === 'object' && 'data' in response.data) {
                    const payload = (response.data as any).data;
                    if (typeof payload === 'string') {
                        try {
                            const decrypted = decryptPayload(payload);
                            response.data = decrypted;
                        } catch (error) {
                            console.error('No fue posible desencriptar la respuesta del servidor.', error);
                            throw error;
                        }
                    }
                }

                return response;
            },
            (error: AxiosError) => {
                if (error.response) {
                    console.error('Error del servidor:', error.response.status, error.response.data);
                } else if (error.request) {
                    console.error('No se recibió respuesta del servidor');
                } else {
                    console.error('Error al configurar la solicitud:', error.message);
                }
                return Promise.reject(error);
            }
        );
    }

    // Método GET
    public async get<T = any>(url: string, config?: SecureAxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.get<T>(url, config);
    }

    // Método POST
    public async post<T = any>(url: string, data?: any, config?: SecureAxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.post<T>(url, data, config);
    }

    // Método PUT
    public async put<T = any>(url: string, data?: any, config?: SecureAxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.put<T>(url, data, config);
    }

    // Método PATCH
    public async patch<T = any>(url: string, data?: any, config?: SecureAxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.patch<T>(url, data, config);
    }

    // Método DELETE
    public async delete<T = any>(url: string, data?: any, config?: SecureAxiosRequestConfig): Promise<AxiosResponse<T>> {
        return this.axiosInstance.delete<T>(url, { ...config, data });
    }

    // Cambiar la URL base
    public setBaseURL(baseURL: string): void {
        this.baseURL = baseURL;
        this.axiosInstance.defaults.baseURL = baseURL;
    }

    // Obtener la URL base actual
    public getBaseURL(): string {
        return this.baseURL;
    }
}

// Exportar instancia singleton
export const request = new Request();

// Exportar la clase para crear instancias personalizadas
export default Request;